// @ts-nocheck
import useProductContext from "@store/Framework/Store/Data/Products/ProductsHooks/useProductContext";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useIsMobile from "@store/Framework/Common/Hooks/useIsMobile";
import { getExtendedCarouselStyles } from "@store/Framework/Common/Utilities/CarouselWidgetHelpers";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import Badge from "@store/MobiusOverrides/Badge/Badge";
import Carousel, {
    CarouselSlide,
    CarouselStyles,
    CustomCarouselClassNames,
} from "@store/Overrides/Components/Carousel";
import ProductPrimaryImage from "@store/Overrides/Components/ProductPrimaryImage";
import {
    OwnProps,
    ProductDetailsPrimaryImageStyles,
} from "@store/Overrides/Widgets/ProductDetails/ProductDetailsPrimaryImage";
import React from "react";
import { useMemo } from "react";
import { css } from "styled-components";

const customCarouselClasses: CustomCarouselClassNames = {
    carouselWrapper: "product-details-primary-carousel-wrapper",
    nextArrowWrapper: "product-details-primary-carousel-next-arrow-wrapper",
    carouselContainer: "product-details-primary-carousel-container",
    carouselSlide: "product-details-primary-carousel-slide",
    carouselSlideInner: "product-details-primary-carousel-slide-inner",
    carouselSlidesContainer: "product-details-primary-carousel-slides-container",
    dotButton: "product-details-primary-carousel-dot-button",
    dotButtonSelected: "product-details-primary-carousel-dot-button-selected",
    dotsWrapper: "product-details-primary-carousel-dots-wrapper",
    prevArrowWrapper: "product-details-primary-carousel-prev-arrow-wrapper",
    carouselDot: "product-details-primary-carousel-dot",
};

const customCarouselClassNameCss: string = Object.values(customCarouselClasses).reduce((acc, className) => {
    acc += `.${className} { 
}
`;
    return acc;
}, "");

interface PrimaryDetailsPrimaryImageCarouselProps {
    selectImageHandler: (index: number) => void;
    imageClickHandler: (event: React.MouseEvent<HTMLImageElement>) => void;
    selectedImageIndex?: number;
    isZoomOpen: boolean;
    fields: OwnProps["fields"];
    productDetailsStyles: ProductDetailsPrimaryImageStyles;
}

const getPrimaryDetailsCarouselStyles = (baseCarouselStyles: CarouselStyles, isMobile: boolean): CarouselStyles => {
    return {
        ...baseCarouselStyles,
        carouselWrapper: {
            css: css`
                ${baseCarouselStyles.carouselWrapper?.css}
                position: relative;
                width: 100%;
                margin-inline: auto;
            `,
        },
        nextArrowWrapper: {
            css: css`
                ${baseCarouselStyles.nextArrowWrapper?.css}
                ${isMobile ? "" : "display: none;"}
            `,
        },
        nextArrowButton: {
            sizeVariant: "large",
            icon: {
                position: "left",
                src: ChevronRight,
            },
            css: css`
                ${baseCarouselStyles.nextArrowButton?.css}
                color: black;
                width: ${pxToRem(48)};
                height: ${pxToRem(48)};
                border: 1px solid ${getColor("common.border")};
                box-shadow: ${({ theme }) => theme.shadows[1]};
                svg {
                    color: black;
                    width: ${pxToRem(24)} !important;
                    height: ${pxToRem(24)} !important;
                }
                background-color: ${getColor("common.background")};
            `,
        },
        prevArrowButton: {
            sizeVariant: "large",
            icon: {
                position: "left",
                src: ChevronLeft,
            },
            css: css`
                ${baseCarouselStyles.prevArrowButton?.css}
                color: black;
                width: ${pxToRem(48)};
                height: ${pxToRem(48)};
                border: ${pxToRem(1)} solid ${getColor("common.border")};
                box-shadow: ${({ theme }) => theme.shadows[1]};
                svg {
                    color: black;
                    width: ${pxToRem(24)} !important;
                    height: ${pxToRem(24)} !important;
                }
                background-color: ${getColor("common.background")};
            `,
        },
        prevArrowWrapper: {
            css: css`
                ${baseCarouselStyles.prevArrowWrapper?.css}
                ${isMobile ? "" : "display: none;"}
            `,
        },
    };
};

interface PrimaryDetailsPrimaryImageCarouselStyles {
    badgeSectionWrapper?: InjectableCss;
    overlayBadgeWrapper?: InjectableCss;
    imageProps?: InjectableCss;
    carouselOverrides?: CarouselStyles;
    primaryImageWrapper?: InjectableCss;
}
const getCursorStyles = (isZoomOpen: boolean, enableZoomContainer: boolean) => {
    if (!enableZoomContainer) {
        return "default";
    }
    return isZoomOpen ? "zoom-out" : "zoom-in";
};
export const primaryDetailsPrimaryImageCarouselStyles: PrimaryDetailsPrimaryImageCarouselStyles = {
    badgeSectionWrapper: {
        css: css`
            position: relative;
        `,
    },
    primaryImageWrapper: {
        css: css`
            width: 100%;
            height: 100%;
        `,
    },
};

const PrimaryDetailsPrimaryImageCarousel: React.FC<PrimaryDetailsPrimaryImageCarouselProps> = ({
    imageClickHandler,
    selectedImageIndex,
    fields,
    productDetailsStyles,
    isZoomOpen,
    selectImageHandler,
}) => {
    const productContext = useProductContext();
    const product = productContext.product;
    const imageNotFoundImage = {
        id: productContext.product.id,
        imageAltText: productContext.product.imageAltText,
        imageType: "Static",
        largeImagePath: productContext.product.largeImagePath,
        mediumImagePath: productContext.product.mediumImagePath,
        smallImagePath: productContext.product.smallImagePath,
    };
    const images = product?.images && product.images.length !== 0 ? product.images : [imageNotFoundImage];
    const styles = productDetailsStyles;
    const isMobile = useIsMobile();
    const extendedCarouselFields = getExtendedCarouselStyles(fields);
    const carouselStyles = getPrimaryDetailsCarouselStyles(extendedCarouselFields, !!isMobile);
    const image = images[selectedImageIndex ?? 0];
    const path =
        image?.mediumImagePath ??
        image?.largeImagePath ??
        productContext.product.largeImagePath ??
        productContext.product.mediumImagePath;
    let { enableZoomContainer } = fields;
    if (images[0] === imageNotFoundImage || isMobile) {
        enableZoomContainer = false;
    }
    const customImageProps: any = {};
    if (!path) {
        customImageProps.onClick = null;
    } else {
        customImageProps.extendedStyles = {
            ...styles.primaryImage,
            image: {
                css: css`
                    ${styles.primaryImage?.image?.css}
                    cursor: ${getCursorStyles(isZoomOpen, enableZoomContainer)};
                `,
            },
        };
    }

    const { overlayBadge, showOverlayBadge, overlayBadgeWrapperStyles } = useOverlayBadge({
        productContext,
        showImageBadge: true,
        showTextBadge: true,
        styles: productDetailsStyles,
    });

    const primaryImageSlides: CarouselSlide[] = useMemo(
        () =>
            images.map(image => ({
                id: image.id,
                renderComponent: () => (
                    <>
                        <ProductPrimaryImage
                            product={product}
                            image={image}
                            useLargeImage
                            {...customImageProps}
                            onClick={enableZoomContainer ? imageClickHandler : undefined}
                        />
                        <StyledWrapper
                            {...primaryDetailsPrimaryImageCarouselStyles.badgeSectionWrapper}
                            className="product-image-primary-badge-Section"
                        >
                            {overlayBadge && showOverlayBadge && (
                                <StyledWrapper
                                    {...overlayBadgeWrapperStyles}
                                    className="product-image-primary-overlay-badge-wrapper"
                                >
                                    <Badge
                                        type={overlayBadge.badgeType}
                                        value={
                                            overlayBadge.badgeType === "Image"
                                                ? overlayBadge.largeImageBadgePath
                                                : overlayBadge.displayText
                                        }
                                        badgeTextColor={overlayBadge.textColorHexCode}
                                        badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                        badgeTextRounding={overlayBadge.badgeStyle === "Round"}
                                        imageAltText={
                                            overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                        }
                                        textBadgeCss={styles.badgeText}
                                        imageBadgeProps={styles.badgeImageProps}
                                    />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </>
                ),
            })),
        [images, enableZoomContainer, customImageProps, product, imageClickHandler, overlayBadge, showOverlayBadge],
    );
    const showCarousel = primaryImageSlides.length > 1;
    return (
        <>
            {showCarousel ? (
                <Carousel
                    carouselClassNames={customCarouselClasses}
                    id="productDetailsPrimaryImageCarousel"
                    slides={primaryImageSlides}
                    extendedStyles={carouselStyles}
                    showDots={false}
                    maxNumberOfColumns={1}
                    startIndex={selectedImageIndex}
                    slideChangeCallback={selectImageHandler}
                />
            ) : (
                <StyledWrapper
                    {...primaryDetailsPrimaryImageCarouselStyles.primaryImageWrapper}
                    className="product-image-primary-wrapper"
                >
                    <ProductPrimaryImage
                        product={product}
                        image={image}
                        useLargeImage
                        {...customImageProps}
                        onClick={enableZoomContainer ? imageClickHandler : undefined}
                    />
                    <StyledWrapper {...primaryDetailsPrimaryImageCarouselStyles.badgeSectionWrapper}>
                        {overlayBadge && showOverlayBadge && (
                            <StyledWrapper
                                {...overlayBadgeWrapperStyles}
                                className="product-image-primary-overlay-badge-wrapper"
                            >
                                <Badge
                                    type={overlayBadge.badgeType}
                                    value={
                                        overlayBadge.badgeType === "Image"
                                            ? overlayBadge.largeImageBadgePath
                                            : overlayBadge.displayText
                                    }
                                    badgeTextColor={overlayBadge.textColorHexCode}
                                    badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                    badgeTextRounding={overlayBadge.badgeStyle === "Round"}
                                    imageAltText={
                                        overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                    }
                                    textBadgeCss={styles.badgeText}
                                    imageBadgeProps={styles.badgeImageProps}
                                />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

export const defaultDetailsCarouselCss = `${customCarouselClassNameCss}
.product-image-primary-wrapper {
}
.product-image-primary-badge-Section {
}
.product-image-primary-overlay-badge-wrapper {
}
`;
export default PrimaryDetailsPrimaryImageCarousel;
